import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useTrackElementClick } from '../../../analytics/cohesion';
import { ELEMENT_TYPES } from '../../../analytics/cohesion/events/element-clicked';
import { LIMITED_TIME_OFFER_BANNER_VARIANT } from '../../../constants/campaignPromoVariants';
import {
  getCheckoutPlanPriceLabel,
  getCheckoutSubscriptionChargeCopy,
} from '../../../constants/elevatedCampaignPromoUi';
import { SUBSCRIPTION_TYPES } from '../../../constants/subscriptions';
import { isValidEmail } from '../../../utils/validation';
import LimitedTimeOfferBanner from '../../CampaignPromo/LimitedTimeOfferBanner';

/**
 * Custom hook for tracking subscription selection clicks
 *
 * Returns a tracking function that can be used in radio button onChange handlers.
 * Tracks subscription selection with proper Cohesion analytics properties.
 *
 * @param {'annual' | 'monthly'} subscriptionType - The type of subscription being tracked
 * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void} Tracking function to use in onChange handler
 *
 * @example
 * const trackAnnualClick = useSubscriptionTracking('annual');
 * <input type="radio" onChange={(e) => { trackAnnualClick(e); onSubscriptionChange(ANNUAL); }} />
 */
const useSubscriptionTracking = (subscriptionType) => {
  const subscription =
    subscriptionType === 'annual'
      ? SUBSCRIPTION_TYPES.ANNUAL
      : SUBSCRIPTION_TYPES.MONTHLY;

  const trackClick = useTrackElementClick({
    actionOutcome: 'Checkout Selection',
    webElement: {
      elementType: ELEMENT_TYPES.RADIO_BUTTONS,
      location: 'Elevated Portal Roster',
      name: subscriptionType,
      text: `${subscription.label} - ${subscription.description} - ${subscription.priceLabel}`,
    },
  });

  return trackClick;
};

const SubscriptionSelector = ({
  selectedSubscription,
  onSubscriptionChange,
  providers = [],
  email = '',
  onEmailChange,
  isProvider = false,
  showCampaignPromo = false,
}) => {
  const providerCount = providers.length;
  const [emailError, setEmailError] = useState('');

  // Tracking hooks for radio buttons
  const trackAnnualClick = useSubscriptionTracking('annual');
  const trackMonthlyClick = useSubscriptionTracking('monthly');

  // Placeholder for future use when calculating totals per provider
  const _calculateTotal = (subscription) => {
    return (subscription.price * providerCount).toFixed(2);
  };

  const validateEmail = (emailValue) => {
    if (!emailValue) {
      setEmailError('');
      return false;
    }
    if (!isValidEmail(emailValue)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    onEmailChange(emailValue);
    validateEmail(emailValue);
  };

  const handleEmailBlur = () => {
    validateEmail(email);
  };

  const chargeCopy = getCheckoutSubscriptionChargeCopy(showCampaignPromo);

  const formatPlanLabel = (subscription) => {
    const practicePrefix = !isProvider ? 'Practice ' : '';
    const priceLabel = showCampaignPromo
      ? getCheckoutPlanPriceLabel(subscription, {
          useCampaignPromo: true,
          isProvider,
        })
      : subscription.priceLabel;
    return `${practicePrefix}${subscription.label} - ${subscription.description} - ${priceLabel}`;
  };

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-4 self-stretch">
      <h2 className="self-stretch font-semibold text-[24px] leading-[36px] text-gray-90">
        Select your subscription
      </h2>

      {/* Account Details Section */}
      <div className="flex flex-col gap-4 self-stretch">
        <h3 className="font-semibold text-[20px] leading-[32px] text-gray-70">
          Account Details
        </h3>
        <div className="flex flex-col gap-4 self-stretch">
          <span className="font-extralight text-sm text-coral-90">
            ***Your email address will be connected to Stripe***
          </span>
          <div className="flex flex-col gap-1 self-stretch">
            <div className="flex gap-2.5 self-stretch">
              <label
                htmlFor="checkout-email"
                className="font-semibold text-sm text-gray-90"
              >
                Email address
              </label>
            </div>
            <div className="flex flex-col gap-1 self-stretch">
              <input
                id="checkout-email"
                type="email"
                value={email || ''}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                placeholder={
                  isProvider
                    ? 'Enter your email address'
                    : 'Administrator email'
                }
                className={`flex items-center gap-2 self-stretch bg-white px-4 py-3 rounded-sm border border-solid ${
                  emailError ? 'border-coral-90' : 'border-gray-70'
                } font-extralight text-base text-gray-70 focus:outline-none focus:border-blue-50`}
              />
              {emailError && (
                <span className="text-sm text-coral-90">{emailError}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Type Section */}
      <h3 className="font-semibold text-[20px] leading-[32px] text-gray-70">
        Subscription Type
      </h3>
      <p className="w-full min-w-0 self-stretch break-words font-extralight text-sm text-gray-90">
        {chargeCopy}
      </p>

      {/* MD-3961: limited-time callout before plan selection (campaign only) */}
      <div className="flex w-full min-w-0 flex-col gap-3 self-stretch">
        {showCampaignPromo && (
          <LimitedTimeOfferBanner
            variant={LIMITED_TIME_OFFER_BANNER_VARIANT.CHECKOUT}
          />
        )}

        <div className="flex w-full min-w-0 flex-col items-stretch gap-5 self-stretch">
          {/* Annual Subscription */}
          <label className="flex shrink-0 cursor-pointer items-start gap-2 self-stretch rounded-lg border-none bg-white p-4 transition-all duration-300">
            <input
              type="radio"
              name="subscription"
              value={SUBSCRIPTION_TYPES.ANNUAL.id}
              checked={
                selectedSubscription?.id === SUBSCRIPTION_TYPES.ANNUAL.id
              }
              onChange={(e) => {
                trackAnnualClick(e);
                onSubscriptionChange(SUBSCRIPTION_TYPES.ANNUAL);
              }}
              className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-blue-50"
              data-qa-target="subscription-annual"
            />
            <span className="min-w-0 flex-1 break-words text-sm font-semibold leading-5 text-gray-90">
              {formatPlanLabel(SUBSCRIPTION_TYPES.ANNUAL)}
            </span>
          </label>

          {/* Monthly Subscription */}
          <label className="flex shrink-0 cursor-pointer items-start gap-2 self-stretch rounded-lg border-none bg-white p-4 transition-all duration-300">
            <input
              type="radio"
              name="subscription"
              value={SUBSCRIPTION_TYPES.MONTHLY.id}
              checked={
                selectedSubscription?.id === SUBSCRIPTION_TYPES.MONTHLY.id
              }
              onChange={(e) => {
                trackMonthlyClick(e);
                onSubscriptionChange(SUBSCRIPTION_TYPES.MONTHLY);
              }}
              className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-blue-50"
              data-qa-target="subscription-monthly"
            />
            <span className="min-w-0 flex-1 break-words text-sm font-semibold leading-5 text-gray-90">
              {formatPlanLabel(SUBSCRIPTION_TYPES.MONTHLY)}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

SubscriptionSelector.propTypes = {
  selectedSubscription: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.number,
    priceLabel: PropTypes.string,
  }),
  onSubscriptionChange: PropTypes.func.isRequired,
  providers: PropTypes.array,
  email: PropTypes.string,
  onEmailChange: PropTypes.func.isRequired,
  isProvider: PropTypes.bool,
  showCampaignPromo: PropTypes.bool,
};

export default SubscriptionSelector;
